import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { getMostFrequentMerchant, getMerchantMonthlySpending } from '../../utils/merchantAnalytics';
import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ProgressDashes from '../../components/ProgressDashes';
import { StoryNavigation } from '../../components/StoryNavigation';
import { useStoryNavigation } from '../../hooks/useStoryNavigation';

const RideOrDiePage = () => {
  const navigate = useNavigate();
  const transactions = useSelector((state: RootState) => state.transactions.items);
  const merchantStats = useMemo(() => getMostFrequentMerchant(transactions), [transactions]);
  
  // Get actual monthly spending data for the most frequent merchant
  const monthlyData = useMemo(() => 
    getMerchantMonthlySpending(transactions, merchantStats.merchantName),
    [transactions, merchantStats.merchantName]
  );

  const maxAmount = Math.max(...monthlyData.map(d => d.amount));
  const highestSpendMonth = useMemo(() => 
    monthlyData.reduce((prev, current) => 
      (prev.amount > current.amount) ? prev : current,
      { month: '', amount: 0 }
    ), [monthlyData]);

  const [hoveredMonth, setHoveredMonth] = useState(highestSpendMonth);

  // Auto-navigate to the next page after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate('/insights/favorite-time');
    }, 10000);

    return () => clearTimeout(timer);
  }, [navigate]);

  const { goToNext, goToPrev, hasNext, hasPrev } = useStoryNavigation();

  return (
    <div>
      {/* Decorative firework - moved to top of component, before content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="fixed bottom-0 right-0 w-48 h-48 opacity-20 -z-10"
      >
        <img src="/firework.svg" alt="" className="w-full h-full" />
      </motion.div>

      <div className="min-h-screen bg-[#221A51]/[0.07] relative overflow-hidden">
        {/* Progress dashes */}
        <ProgressDashes currentStep={4} totalSteps={11} />

        <StoryNavigation
          onNext={goToNext}
          onPrev={goToPrev}
          hasNext={hasNext}
          hasPrev={hasPrev}
        />

        <div className="px-4 sm:px-16 pt-16 sm:pt-20 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-1 sm:space-y-2"
          >
            <h1 className="text-2xl sm:text-[34px] leading-[140%] font-black text-[#221A51]">
              Ride or Die
            </h1>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-8 sm:mt-12 space-y-4 sm:space-y-6"
          >
            {/* Shop and visits card */}
            <div className="bg-white rounded-xl p-4 sm:p-6 relative border-2 border-[#221A51] shadow-[4px_4px_0px_0px_#221A51]">
              <div className="flex justify-between items-center">
                <div className="flex-1 min-w-0">
                  <div className="text-[#221A51]/60 mb-1 sm:mb-2 text-sm sm:text-base">Shop</div>
                  <div className="text-[#221A51] text-lg sm:text-2xl font-bold truncate">
                    {merchantStats.merchantName}
                  </div>
                </div>
                <div className="ml-4 text-right">
                  <div className="text-[#221A51]/60 mb-1 sm:mb-2 text-sm sm:text-base">Total visits</div>
                  <div className="text-[#221A51] text-lg sm:text-2xl font-bold">
                    {merchantStats.visitCount}
                  </div>
                </div>
              </div>
            </div>

            {/* Monthly spending chart */}
            <div className="bg-white rounded-xl p-4 sm:p-6 relative border-2 border-[#221A51] shadow-[4px_4px_0px_0px_#221A51]">
              <div className="mb-3 sm:mb-4">
                <div className="text-[#221A51] text-lg sm:text-2xl font-bold">
                  {merchantStats.totalSpent.toLocaleString()}$
                </div>
                <div className="text-[#221A51]/60 text-sm sm:text-base">Total amount of money spent</div>
              </div>

              <div className="relative">
                {/* Single Tooltip */}
                {hoveredMonth && (
                  <div 
                    className="absolute bg-[#FF9B7B] text-white px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm whitespace-nowrap z-10"
                    style={{
                      bottom: '100%',
                      left: `${(monthlyData.findIndex(d => d.month === hoveredMonth.month) / (monthlyData.length - 1)) * 100}%`,
                      transform: 'translateX(-50%) translateY(-8px)'
                    }}
                  >
                    {hoveredMonth.month} ${hoveredMonth.amount}
                  </div>
                )}

                {/* Bar chart */}
                <div className="flex items-end justify-between h-36 sm:h-48 mt-6 sm:mt-8 gap-1 sm:gap-2">
                  {monthlyData.map((data, index) => {
                    const heightPercentage = (data.amount / maxAmount);
                    const heightInPixels = Math.max(4, heightPercentage * (window.innerWidth < 640 ? 120 : 160));

                    return (
                      <div
                        key={data.month}
                        className="relative flex flex-col items-center flex-1"
                        onMouseEnter={() => setHoveredMonth(data)}
                        onMouseLeave={() => setHoveredMonth(highestSpendMonth)}
                      >
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: heightInPixels }}
                          transition={{ delay: 0.3 + index * 0.1 }}
                          className="w-2 sm:w-3 bg-[#9B86EA] rounded-full"
                          style={{
                            height: `${heightInPixels}px`
                          }}
                        />
                        <div className="mt-2 text-xs sm:text-sm text-[#221A51]/60">{data.month}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RideOrDiePage;
